<template>
  <div v-if="showChannelPicker" class="form-group">
    <div class="label required fs-xl">Distribution Channel</div>
    <div class="row">
      <div v-for="(item, i) in distributionChannels" :key="i" :class="colClass">
        <input
          :id="'type_' + item.code"
          v-model="channel"
          :checked="channel === item.code"
          :value="item.code"
          name="distribution_channel"
          type="radio"
        />
        <label :for="'type_' + item.code" class="radio-group">
          <span class="radio-title">
            <span v-if="item.icon" :class="item.icon" />
            {{ item.name }}
          </span>
          <span class="radio-description">{{ item.description }}</span>
          <span v-if="item.beta" class="company-name-info">
            <img src="@/assets/img/info-circle-outlined.svg" alt="" class="mr-1" /> beta
          </span>
        </label>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { DistributionChannelConfig } from '@/types/Models/Assignment'
import { ref, computed, defineComponent, watch, type ComputedRef } from 'vue'
import { ECONOMY, PRIORITY } from '@/const/AssignmentServiceLevels'
import { BOX, LETTER, PACKAGE, PARCEL } from '@/const/DistributionChannels'
import { useAccountStore } from '@/store/account'
import { useAssignmentFormStore } from '@/store/assignmentForm'
import { useCustomersStore } from '@/store/customers'

export default defineComponent({
  name: 'DistributionChannel',
  setup() {
    const allChannels: DistributionChannelConfig[] = [
      { code: PACKAGE, name: 'Packet', description: 'Mailbox delivery (or to the door step) – Up to 5 kg.', beta: false, staffOnly: false, icon: 'fa fa-book' },
      { code: PARCEL, name: 'Parcel', description: 'Choose between delivery to Service point (B2C) or by Carrier (B2B).', beta: false, staffOnly: false, icon: 'fa fa-cube' },
      { code: BOX, name: 'Parcel locker', description: 'Parcel lockers located outside or indoors.', beta: true, staffOnly: true, icon: 'fa fa-lock' },
      { code: LETTER, name: 'Letter', description: 'Up to 2 kg.', beta: true, staffOnly: true, icon: 'fa fa-envelope' },
    ]

    const colClasses: Record<number, string> = {
      1: 'col-12',
      2: 'col-12 col-md-6',
      3: 'col-12 col-md-4',
      4: 'col-12 col-md-3',
    }

    const assignmentFormStore = useAssignmentFormStore()
    const accountStore = useAccountStore()
    const customersStore = useCustomersStore()

    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const companyId = computed(() => assignmentFormStore.assignment.company_id)

    const allowedChannels = computed<Record<string, boolean> | null>(() => {
      if (isStaff.value) {
        const company = customersStore.allCustomers.find((c) => c.id === companyId.value)
        return company?.allowedDistributionChannels ?? null
      }
      return customersStore.customer?.allowedDistributionChannels ?? null
    })

    const distributionChannels: ComputedRef<DistributionChannelConfig[]> = computed(() => {
      const allowed = allowedChannels.value
      return allChannels.filter((c) => {
        if (!isStaff.value && c.staffOnly) return false
        return !(allowed !== null && allowed[c.code] === false);
      })
    })

    const colClass = computed(() => colClasses[distributionChannels.value.length] ?? 'col-12 col-md-3')
    const showChannelPicker = computed(() => distributionChannels.value.length > 1)

    const channel = ref(PACKAGE)

    watch(
      distributionChannels,
      (channels) => {
        if (!channels.find((c) => c.code === channel.value)) {
          channel.value = channels[0]?.code ?? PACKAGE
        }
      },
      { immediate: true },
    )

    watch(
      channel,
      (value) => {
        assignmentFormStore.setFields({ distribution_channel: value })
        switch (value) {
          case PARCEL:
            // always send service level priority for parcel assignments
            assignmentFormStore.setFields({ service_level: PRIORITY })
            break
          case LETTER:
            // always send service level economy for letter assignments
            assignmentFormStore.setFields({ service_level: ECONOMY })
            break
          case PACKAGE:
            assignmentFormStore.resetPieces()
            break
        }
      },
      { immediate: true },
    )

    return {
      distributionChannels,
      showChannelPicker,
      colClass,
      channel,
    }
  },
})
</script>
